import { MedplumClient } from "@medplum/core";
import { Bundle, Encounter } from "@medplum/fhirtypes";

import { collectResourcesFromPaginatedBundle } from "@/utils/pagination";

import {
  EncounterServiceDateDiffContext,
  getEncounterServiceDateDiffContextFromEncounters,
} from "./encounter-service-date-diff";
import { parseIsoInstant } from "./questionnaire-response-instant";

/** Encounter is `finished` from Sign & Lock onward; earlier documentation versions are ignored. */
const ENCOUNTER_FINISHED_STATUS: Encounter["status"] = "finished";

export interface EncounterDetailVersionPair {
  /** Encounter state left behind by the amendment being viewed. */
  currentEncounter?: Encounter;
  /** Encounter state going into the amendment being viewed (Sign & Lock for the first amendment). */
  previousEncounter?: Encounter;
}

/**
 * Reads the full Encounter version history, following pagination links when present.
 *
 * Throws when a later page fails. History comes back newest first, so a partial read drops the
 * oldest versions — the Sign & Lock transition that the signed date and amendment diffs depend on.
 */
export async function readEncounterHistory(
  medplum: MedplumClient,
  encounterId: string,
): Promise<Encounter[]> {
  const initialBundle: Bundle<Encounter> = await medplum.readHistory("Encounter", encounterId);

  return collectResourcesFromPaginatedBundle(medplum, initialBundle, "Encounter", {
    throwOnPageError: true,
  });
}

/** Encounter-information display values for a version diff, derived from two Encounter versions. */
export interface EncounterInfoDiffFields {
  currentTemplate?: string;
  previousTemplate?: string;
  currentType?: string;
  previousType?: string;
  currentLocation?: string;
  previousLocation?: string;
  serviceDateDiff: EncounterServiceDateDiffContext | null;
}

/**
 * Reads the standard Encounter Information fields (template/type/location/service date) straight
 * from two Encounter versions, so the diff reflects the amendment's own change rather than the
 * live (latest) Encounter. Shared by the encounter-type version diffs that map these fields to
 * `serviceType` / `type` / `class` / `period`.
 */
export function getEncounterInfoFieldsFromEncounters(
  currentEncounter: Encounter,
  previousEncounter: Encounter,
): EncounterInfoDiffFields {
  return {
    currentTemplate: currentEncounter.serviceType?.coding?.[0]?.display,
    previousTemplate: previousEncounter.serviceType?.coding?.[0]?.display,
    currentType: currentEncounter.type?.[0]?.text,
    previousType: previousEncounter.type?.[0]?.text,
    currentLocation: currentEncounter.class?.display,
    previousLocation: previousEncounter.class?.display,
    serviceDateDiff: getEncounterServiceDateDiffContextFromEncounters(
      currentEncounter,
      previousEncounter,
    ),
  };
}

/**
 * Resolves the Encounter Information fields for a version diff, preferring the Encounter version
 * pair (the accurate source) and falling back to the questionnaire answers when the pair is
 * unavailable. The answers mapping varies per encounter type (different link ids / answer shapes),
 * so it is supplied by the caller; the encounter mapping defaults to the standard
 * {@link getEncounterInfoFieldsFromEncounters} but can be overridden for encounter types that map
 * the fields differently (e.g. Transfer of Care using `reasonCode`).
 */
export function resolveEncounterInfoFields(
  currentEncounter: Encounter | undefined,
  previousEncounter: Encounter | undefined,
  getFieldsFromAnswers: () => EncounterInfoDiffFields,
  getFieldsFromEncounters: (
    currentEncounter: Encounter,
    previousEncounter: Encounter,
  ) => EncounterInfoDiffFields = getEncounterInfoFieldsFromEncounters,
): EncounterInfoDiffFields {
  if (currentEncounter && previousEncounter) {
    return getFieldsFromEncounters(currentEncounter, previousEncounter);
  }
  return getFieldsFromAnswers();
}

function getEncounterChangeInstant(encounter: Encounter): number | undefined {
  return parseIsoInstant(encounter.meta?.lastUpdated);
}

function dedupeEncounterHistoryByVersionId(encounters: Encounter[]): Encounter[] {
  const seenVersionIds = new Set<string>();
  return encounters.filter((encounter) => {
    const versionId = encounter.meta?.versionId;
    if (!versionId) {
      return true;
    }
    if (seenVersionIds.has(versionId)) {
      return false;
    }
    seenVersionIds.add(versionId);
    return true;
  });
}

function findLast<T>(items: T[], predicate: (item: T) => boolean): T | undefined {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index])) {
      return items[index];
    }
  }
  return undefined;
}

/**
 * Resolves the pair of Encounter versions to diff on an amendment view.
 *
 * Only versions from Sign & Lock onward are considered (status `finished`); earlier in-progress
 * documentation versions are dropped. Pairing is by change instant rather than index because the
 * Encounter is only re-versioned when an amendment actually changes an encounter detail, so the
 * finished-version count does not line up with the amendment count.
 *
 * - `previousEncounter` — newest finished version at or before the amendment (the state going into
 *   it; the Sign & Lock version for the first amendment).
 * - `currentEncounter` — newest finished version before the next amendment (the state this
 *   amendment left behind). Equal to `previousEncounter` when the amendment changed no encounter
 *   detail, so the diff renders no change.
 *
 * @param encounterHistory Full Encounter version history, in any order.
 * @param currentAmendmentInstant Change instant of the amendment being viewed.
 * @param nextAmendmentInstant Change instant of the next-newer amendment, if any.
 */
export function resolveEncounterDetailVersionPairForDiff(
  encounterHistory: Encounter[],
  currentAmendmentInstant: number | undefined,
  nextAmendmentInstant?: number,
): EncounterDetailVersionPair {
  if (currentAmendmentInstant === undefined) {
    return {};
  }

  const finishedVersionsOldestFirst = dedupeEncounterHistoryByVersionId(
    encounterHistory.filter((encounter) => encounter.status === ENCOUNTER_FINISHED_STATUS),
  )
    .map((encounter) => ({ encounter, instant: getEncounterChangeInstant(encounter) }))
    .filter(
      (entry): entry is { encounter: Encounter; instant: number } => entry.instant !== undefined,
    )
    .sort((first, second) => first.instant - second.instant);

  const upperBound = nextAmendmentInstant ?? Number.POSITIVE_INFINITY;

  const previousEncounter = findLast(
    finishedVersionsOldestFirst,
    (entry) => entry.instant <= currentAmendmentInstant,
  )?.encounter;

  const currentEncounter = findLast(
    finishedVersionsOldestFirst,
    (entry) => entry.instant < upperBound,
  )?.encounter;

  return { currentEncounter, previousEncounter };
}
